// FollowButton.jsx
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { ButtonId } from '../resource/trackingConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import ParticleBurst, { BURST_DURATION } from '../component/ParticleBurst.jsx';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import media from '../style/media.js';
import { color } from '../style/variables.js';

import BlackAddIconSource from '../../img/ic_add_black.svg';
import WhiteCheckIconSource from '../../img/ic_complete_white_xs.svg';
import GrayCheckIconSource from '../../img/ic_complete_gray_xs.svg';

const defaultObject = {};
const defaultFunction = () => null;

// The spec swaps two whole button variants, so the scale belongs to the button
// itself with the labels only crossfading inside it.
const BUTTON_POP_DURATION = 500;
const LABEL_FADE_OUT_DURATION = 150;
const LABEL_FADE_IN_DELAY = 100;
const LABEL_FADE_IN_DURATION = 150;

export const Theme = {
  DARK: 'dark',
  LIGHT: 'light',
};

export const FollowButton = ({
  meId = null,
  userId = null,
  theme = Theme.DARK,
  isFollowing = null,
  isBigSize = false,
  shouldKeepDisplay = false,
  shouldShowIcon = false,
  mixpanelTrackingPayload = defaultObject,
  sendFollowUser = defaultFunction,
  sendUnfollowUser = defaultFunction,
  fetchUserIsFollowing = defaultFunction,
}) => {
  const { t } = useTranslation();
  const [shouldShow, setShouldShow] = useState(
    isFollowing === false || !meId || shouldKeepDisplay
  );
  const [isFollowSucceedAnimating, setIsFollowSucceedAnimating] =
    useState(false);
  // The burst portals itself out of the button and measures this box.
  const buttonRef = useRef(null);
  useEffect(() => {
    if (meId && userId && isFollowing == null) {
      fetchUserIsFollowing({ userId });
    }
  }, [meId, userId, isFollowing, fetchUserIsFollowing]);
  useEffect(() => {
    if (isFollowing === false) {
      setShouldShow(true);
    }
  }, [isFollowing]);
  if (!shouldShow) {
    return null;
  }
  const handleClickFollow = async () => {
    setIsFollowSucceedAnimating(true);
    // Awaited alongside the request, not on the timer alone: on a slow network
    // the label would flip back to "follow" and then flip again once the
    // response lands. Rejections are swallowed so it cannot stay stuck.
    await Promise.all([
      new Promise(resolve => setTimeout(resolve, BURST_DURATION)),
      sendFollowUser({ userId }),
    ]).catch(() => null);
    setIsFollowSucceedAnimating(false);
  };
  const renderLabel = ({ isFollowingLabel }) => (
    <>
      {shouldShowIcon ? (
        <img
          aria-hidden
          src={
            !isFollowingLabel
              ? BlackAddIconSource
              : Theme.DARK === theme
                ? WhiteCheckIconSource
                : GrayCheckIconSource
          }
        />
      ) : null}
      {t(isFollowingLabel ? 'following' : 'follow', {
        ns: TranslationNamespace.GENERAL,
      })}
    </>
  );
  return (
    <StyledFollowButton
      ref={buttonRef}
      theme={theme}
      isFollowing={isFollowing || isFollowSucceedAnimating}
      isBigSize={isBigSize}
      $isAnimating={isFollowSucceedAnimating}
      data-element_id={
        isFollowing ? ButtonId.All.ButtonUnfollow : ButtonId.All.ButtonFollow
      }
      data-tracking_payload={mixpanelTrackingPayload}
      onClick={() => {
        if (isFollowing) {
          return sendUnfollowUser({ userId });
        }
        // Logged out taps open the login flow first, where a followed
        // confirmation would be wrong.
        if (!meId) {
          return sendFollowUser({ userId });
        }
        return handleClickFollow();
      }}
    >
      {isFollowSucceedAnimating ? (
        <>
          <FadingOutLabel aria-hidden>
            {renderLabel({ isFollowingLabel: false })}
          </FadingOutLabel>
          <PoppingInLabel>
            {renderLabel({ isFollowingLabel: true })}
          </PoppingInLabel>
        </>
      ) : (
        renderLabel({ isFollowingLabel: isFollowing })
      )}
      <ParticleBurst
        isActive={isFollowSucceedAnimating}
        anchorRef={buttonRef}
      />
    </StyledFollowButton>
  );
};

FollowButton.propTypes = {
  meId: PropTypes.string,
  userId: PropTypes.string,
  theme: PropTypes.string,
  isFollowing: PropTypes.bool,
  isBigSize: PropTypes.bool,
  shouldKeepDisplay: PropTypes.bool,
  shouldShowIcon: PropTypes.bool,
  mixpanelTrackingPayload: PropTypes.object,
  sendFollowUser: PropTypes.func,
  sendUnfollowUser: PropTypes.func,
  fetchUserIsFollowing: PropTypes.func,
};

const UnfollowingStyle = {
  borderColor: color.white,
  backgroundColor: color.white,
  color: color.neutral[0],
};

const DarkFollowingStyle = {
  borderColor: color.neutral[100],
  backgroundColor: color.neutral['10_alpha_40'],
  color: color.neutral[100],
};

const LightFollowingStyle = {
  borderColor: color.neutral[0],
  backgroundColor: 'rgba(246, 246, 246, 0.2)',
  color: color.neutral[0],
};

const StyledFollowButton = styled.button.attrs(
  ({ isFollowing, isBigSize, theme }) => ({
    style: {
      width: isBigSize ? '100%' : 'auto',
      height: `${isBigSize ? 36 : 24}px`,
      fontWeight: isBigSize ? 600 : 400,
      ...(!isFollowing
        ? UnfollowingStyle
        : Theme.DARK === theme
          ? DarkFollowingStyle
          : LightFollowingStyle),
    },
  })
)`
  ${ResetButtonStyle};
  position: relative;
  border-radius: 4px;
  border-style: solid;
  border-width: 1px;
  padding: 0px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ $isAnimating }) =>
    $isAnimating &&
    css`
      pointer-events: none;
      /* Keeps the overshoot from being overlapped by a following sibling. */
      z-index: 1;
      animation: ${PopButtonKeyframes} ${BUTTON_POP_DURATION}ms ease-out;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}
  img {
    margin-right: 4px;
    width: ${({ isBigSize }) => (isBigSize ? 20 : 12)}px;
    height: ${({ isBigSize }) => (isBigSize ? 20 : 12)}px;
    object-fit: contain;
  }
  ${({ isBigSize }) => {
    if (isBigSize) {
      return css`
        font-size: 20px;
        ${media.mobile`
          font-size: 18px;
        `};
      `;
    }
    return css`
      font-size: 12px;
    `;
  }}
`;

// Baking the spec's 1.08 overshoot into the values lets a plain ease-out
// reproduce its spring without simulating the physics.
const PopButtonKeyframes = keyframes`
  0% { transform: scale(1); }
  30% { transform: scale(0.9); }
  40% { transform: scale(0.85); }
  60% { transform: scale(1.08); }
  100% { transform: scale(1); }
`;

const FadeOutLabelKeyframes = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const FadeInLabelKeyframes = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Lifted out of the flow so the incoming label keeps sizing the button. Scale
// is left to the button so the two do not compound.
const FadingOutLabel = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${FadeOutLabelKeyframes} ${LABEL_FADE_OUT_DURATION}ms
    cubic-bezier(0, 0, 0.58, 1) forwards;

  @media (prefers-reduced-motion: reduce) {
    opacity: 0;
    animation: none;
  }
`;

const PoppingInLabel = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  animation: ${FadeInLabelKeyframes} ${LABEL_FADE_IN_DURATION}ms
    cubic-bezier(0, 0, 0.58, 1) ${LABEL_FADE_IN_DELAY}ms forwards;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
  }
`;

export default FollowButton;
